import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Formate une Date en "YYYY-MM-DD" à partir de ses composantes LOCALES (jamais toISOString,
// qui convertit en UTC et fait glisser d'un jour toute date locale de minuit dans un fuseau
// en avance sur UTC — le cas de la France toute l'année).
export function toLocalISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

// Parse une chaîne "YYYY-MM-DD" en Date locale (jamais new Date(iso), qui interprète les
// dates sans heure comme minuit UTC — un décalage inverse mais tout aussi faux).
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d)
}

// Lundi (minuit, heure locale) de la semaine contenant `d`. Source unique de la
// convention de semaine calendaire : la tuile Strain, le graphe Strain, les vues
// « jour »/« semaine » du Monitoring et la page admin doivent toutes s'ancrer au
// MÊME lundi, sinon elles se remettent à afficher des découpages différents sous
// les mêmes libellés — exactement le bug que le chantier Strain calendaire corrige.
export function startOfWeek(d: Date = new Date()): Date {
  const day = d.getDay()
  const monday = new Date(d)
  monday.setDate(d.getDate() + ((day === 0 ? -6 : 1) - day))
  monday.setHours(0, 0, 0, 0)
  return monday
}

// Nombre au format français : séparateur décimal virgule, pas de séparateur de milliers.
// Source unique — DashboardPage et ProgressPage s'appuient dessus (comme NAV_ITEMS/BottomNav).
export function formatFr(n: number, decimals = 1): string {
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  })
}

// Entier au format français AVEC séparateur de milliers (espace fine insécable) : « 2 663 ».
// Pages Nutrition, où les kcal dépassent le millier ; formatFr reste sans séparateur.
export function formatIntFr(n: number): string {
  return Math.round(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })
}

// Date courte en français : « 20 juil. 2026 ». Utilisée par les lignes de repère temporel
// du Profil, où la place manque — la forme longue (« 20 juillet 2026 ») y ferait passer la
// ligne sur deux niveaux à 10 px. Parse les composantes à la main plutôt que new Date(iso),
// qui lirait une date sans heure comme minuit UTC et la ferait glisser d'un jour.
export function formatDateShortFr(iso: string): string {
  const [y, m, d] = iso.split("T")[0].split("-").map(Number)
  if (!y || !m || !d) return "—"
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}
